import { useId } from 'react';

import type { CalloutModel, CalloutOpen } from './overview-landing';

/**
 * The callout a landing leaves on the Overview (twin slice 1, plan W4 step
 * 10; catalogue §8): what the link pointed at, where it is, and what is
 * known about it, in DOM text at least 15 px (`OverviewScreen.css`).
 *
 * It never opens a drawer. "Open <name> panel" calls the host's own open
 * handler -- a tap the person makes, exactly like a tap on the canvas --
 * and × (a 44 px target) closes the callout, not the drawer.
 */
export function OverviewCallout({
  model,
  onClose,
  onOpen,
}: {
  readonly model: CalloutModel;
  readonly onClose: () => void;
  readonly onOpen: (open: CalloutOpen) => void;
}) {
  const titleId = useId();
  return (
    <section
      className="nx-overview-callout"
      aria-labelledby={titleId}
      data-testid="overview-callout"
    >
      <div className="nx-overview-callout__head">
        <p className="nx-overview-callout__title" id={titleId}>
          {model.title}
        </p>
        <button
          type="button"
          className="nx-overview-callout__close"
          aria-label="Close"
          onClick={onClose}
          data-testid="overview-callout-close"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {/* A live region, so "Finding SL1…" turning into an answer is read
          out without moving focus. */}
      <div
        className="nx-overview-callout__lines"
        role="status"
        data-testid="overview-callout-lines"
      >
        {model.lines.map((line, i) => (
          <p key={`${i}:${line}`}>{line}</p>
        ))}
      </div>
      {model.open ? (
        <button
          type="button"
          className="nx-overview-callout__open"
          onClick={() => onOpen(model.open!)}
          data-testid="overview-callout-open"
        >
          {model.open.label}
        </button>
      ) : null}
    </section>
  );
}
